/**
 * Validation métier d'un sondage de dates créé par une IA (serveur MCP) ou
 * par le lien pré-rempli `/new`. TS pur : pas de zod (le serveur MCP est en
 * zod 4, `packages/core` en zod 3 — spec §5.2), pas de React, pas de Supabase.
 *
 * Deux modes :
 * - `strict` (MCP) : tout problème est une ERREUR avec un code stable ; l'IA
 *   corrige et rappelle l'outil.
 * - `lenient` (`/new`) : on garde tout ce qui est valide et on signale le reste
 *   en AVERTISSEMENTS (« 2 dates passées ignorées »), jamais de page d'erreur
 *   vide (spec §3.4). Restent des erreurs, en souple, les deux seuls manques
 *   qui empêchent de publier tel quel : titre vide, aucune date valide. Le
 *   `value` est rendu quand même, pour pré-remplir l'éditeur.
 */
import {
  aiPollError,
  aiPollIssue,
  type AiPollError,
  type AiPollIssue,
} from "./errors.js";
import {
  addDays,
  addMonths,
  AI_POLL_TIMEZONE,
  hasOverlap,
  isRealIsoDate,
  normalizeRanges,
  parseTimeRange,
  todayInTimeZone,
  type TimeRange,
} from "./dates.js";
import {
  AI_POLL_DEFAULT_LANGUAGE,
  isAiPollLanguage,
  type AiPollLanguage,
} from "./messages.js";

export const AI_POLL_LIMITS = {
  titleMax: 120,
  descriptionMax: 500,
  maxDates: 31,
  maxSlotsPerDate: 6,
  maxOptions: 100,
  horizonMonths: 18,
  /** Une date d'HIER (Europe/Paris) est encore acceptée (fuseaux à l'ouest). */
  pastToleranceDays: 1,
} as const;

export type ValidationMode = "strict" | "lenient";

export interface PollInput {
  title?: string | null;
  /** Dates ISO `YYYY-MM-DD`. */
  dates?: ReadonlyArray<string> | null;
  /** `HH:MM-HH:MM`, appliqués à toutes les dates sans entrée dans `timesByDate`. */
  timeSlots?: ReadonlyArray<string> | null;
  /**
   * Créneaux propres à une date, PRIORITAIRES sur `timeSlots`. Une date
   * présente ici mais absente de `dates` est ajoutée au sondage. Une liste
   * vide signifie « journée entière » pour cette date.
   */
  timesByDate?: Readonly<Record<string, ReadonlyArray<string>>> | null;
  description?: string | null;
  language?: string | null;
}

export interface ValidationContext {
  now?: Date;
  /** Fuseau de référence pour « aujourd'hui ». Défaut : Europe/Paris. */
  tz?: string;
  mode?: ValidationMode;
  /** Langue des messages ; défaut : `input.language` si valide, sinon anglais. */
  messageLanguage?: AiPollLanguage;
}

export interface ValidatedPoll {
  title: string;
  description: string | null;
  language: AiPollLanguage | null;
  /** Triées, sans doublon. */
  dates: string[];
  /** Seulement les dates qui ont au moins un créneau (les autres = journée entière). */
  slotsByDate: Record<string, TimeRange[]>;
  /** Nombre d'options de vote : un par créneau, ou un par date sans créneau. */
  optionsCount: number;
}

export interface ValidationResult {
  ok: boolean;
  value: ValidatedPoll;
  warnings: AiPollIssue[];
  errors: AiPollError[];
}

/** Longueur en points de code (un emoji simple = 1). */
function length(value: string): number {
  return Array.from(value).length;
}

function truncate(value: string, max: number): string {
  return Array.from(value).slice(0, max).join("");
}

export function normalizeTitle(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return (
    raw
      .normalize("NFC")
      // Retours à la ligne et tabulations → espace ; autres caractères de
      // contrôle retirés (un titre s'affiche sur une ligne).
      .replace(/[\r\n\t]+/g, " ")
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f]/g, "")
      .trim()
  );
}

export function validatePollInput(
  input: PollInput,
  ctx: ValidationContext = {},
): ValidationResult {
  const mode: ValidationMode = ctx.mode ?? "strict";
  const strict = mode === "strict";
  const now = ctx.now ?? new Date();
  const tz = ctx.tz ?? AI_POLL_TIMEZONE;
  const L = AI_POLL_LIMITS;

  const pollLanguage = isAiPollLanguage(input.language) ? input.language : null;
  const language: AiPollLanguage =
    ctx.messageLanguage ?? pollLanguage ?? AI_POLL_DEFAULT_LANGUAGE;

  const errors: AiPollError[] = [];
  const warnings: AiPollIssue[] = [];

  // ---- Titre -------------------------------------------------------------
  let title = normalizeTitle(input.title);
  if (length(title) === 0) {
    errors.push(
      aiPollError("TITLE_INVALID", {
        messageKey: strict ? "errors.title_invalid" : "warnings.title_missing",
        details: { field: "title" },
        language,
      }),
    );
  } else if (length(title) > L.titleMax) {
    if (strict) {
      errors.push(
        aiPollError("TITLE_INVALID", {
          details: { field: "title", length: length(title), max: L.titleMax },
          language,
        }),
      );
    } else {
      title = truncate(title, L.titleMax).trim();
      warnings.push(
        aiPollIssue("TITLE_INVALID", "warnings.title_truncated", {
          params: { max: L.titleMax },
          details: { field: "title" },
          language,
        }),
      );
    }
  }

  // ---- Description -------------------------------------------------------
  let description: string | null =
    typeof input.description === "string"
      ? input.description.normalize("NFC").trim() || null
      : null;
  if (description && length(description) > L.descriptionMax) {
    if (strict) {
      errors.push(
        aiPollError("INVALID_FORMAT", {
          messageKey: "errors.description_too_long",
          params: { max: L.descriptionMax },
          details: { field: "description", length: length(description) },
          language,
        }),
      );
    } else {
      description = truncate(description, L.descriptionMax).trim();
      warnings.push(
        aiPollIssue("INVALID_FORMAT", "warnings.description_truncated", {
          params: { max: L.descriptionMax },
          details: { field: "description" },
          language,
        }),
      );
    }
  }

  // ---- Langue ------------------------------------------------------------
  if (
    input.language !== undefined &&
    input.language !== null &&
    input.language !== "" &&
    !pollLanguage
  ) {
    warnings.push(
      aiPollIssue("LANGUAGE_UNSUPPORTED", "warnings.language_unsupported", {
        params: { value: String(input.language) },
        details: { field: "language" },
        language,
      }),
    );
  }

  // ---- Dates -------------------------------------------------------------
  const today = todayInTimeZone(now, tz);
  const minDate = addDays(today, -L.pastToleranceDays);
  const maxDate = addMonths(today, L.horizonMonths);

  const rawDates: string[] = [];
  for (const d of input.dates ?? []) rawDates.push(String(d).trim());
  for (const d of Object.keys(input.timesByDate ?? {})) {
    if (!rawDates.includes(d.trim())) rawDates.push(d.trim());
  }

  const dropped = {
    invalid: [] as string[],
    past: [] as string[],
    far: [] as string[],
  };
  const kept = new Set<string>();
  for (const raw of rawDates) {
    if (!isRealIsoDate(raw)) {
      dropped.invalid.push(raw);
      if (strict)
        errors.push(
          aiPollError("INVALID_FORMAT", {
            messageKey: "errors.invalid_date",
            params: { value: raw },
            details: { field: "dates", value: raw },
            language,
          }),
        );
    } else if (raw < minDate) {
      dropped.past.push(raw);
      if (strict)
        errors.push(
          aiPollError("DATE_IN_PAST", {
            params: { date: raw },
            details: { field: "dates", date: raw, today, timezone: tz },
            language,
          }),
        );
    } else if (raw > maxDate) {
      dropped.far.push(raw);
      if (strict)
        errors.push(
          aiPollError("DATE_TOO_FAR", {
            params: { date: raw },
            details: { field: "dates", date: raw, maxDate },
            language,
          }),
        );
    } else {
      kept.add(raw);
    }
  }
  if (!strict) {
    const agg: Array<
      [string[], string, "INVALID_FORMAT" | "DATE_IN_PAST" | "DATE_TOO_FAR"]
    > = [
      [dropped.invalid, "warnings.invalid_dates_dropped", "INVALID_FORMAT"],
      [dropped.past, "warnings.past_dates_dropped", "DATE_IN_PAST"],
      [dropped.far, "warnings.far_dates_dropped", "DATE_TOO_FAR"],
    ];
    for (const [list, key, code] of agg) {
      if (list.length > 0)
        warnings.push(
          aiPollIssue(code, key, {
            params: { count: list.length },
            details: { field: "dates", values: list },
            language,
          }),
        );
    }
  }
  let dates = [...kept].sort();

  // ---- Créneaux ----------------------------------------------------------
  const invalidSlots: string[] = [];
  const truncatedSlotLists: string[] = [];

  const parseList = (
    list: ReadonlyArray<string>,
    scope: string,
  ): TimeRange[] => {
    const ranges: TimeRange[] = [];
    for (const raw of list) {
      const r = parseTimeRange(raw, mode);
      if (r) ranges.push(r);
      else {
        invalidSlots.push(String(raw));
        if (strict)
          errors.push(
            aiPollError("INVALID_FORMAT", {
              messageKey: "errors.invalid_slot",
              params: { value: String(raw) },
              details: { field: "time_slots", scope, value: raw },
              language,
            }),
          );
      }
    }
    let normalized = normalizeRanges(ranges);
    if (normalized.length > L.maxSlotsPerDate) {
      if (strict) {
        errors.push(
          aiPollError("TOO_MANY_OPTIONS", {
            messageKey: "errors.too_many_slots",
            params: { max: L.maxSlotsPerDate },
            details: { field: "time_slots", scope, count: normalized.length },
            language,
          }),
        );
      } else {
        truncatedSlotLists.push(scope);
      }
      normalized = normalized.slice(0, L.maxSlotsPerDate);
    }
    return normalized;
  };

  const globalSlots = parseList(input.timeSlots ?? [], "all");
  if (hasOverlap(globalSlots)) {
    warnings.push(
      aiPollIssue("SLOTS_OVERLAP", "warnings.slots_overlap_all", {
        details: { field: "time_slots", scope: "all" },
        language,
      }),
    );
  }

  const perDate = new Map<string, TimeRange[]>();
  for (const [rawKey, list] of Object.entries(input.timesByDate ?? {})) {
    const key = rawKey.trim();
    const slots = parseList(Array.isArray(list) ? list : [], key);
    // Les créneaux d'une date écartée (illisible, passée…) partent avec elle.
    if (!kept.has(key)) continue;
    perDate.set(key, slots);
    if (hasOverlap(slots)) {
      warnings.push(
        aiPollIssue("SLOTS_OVERLAP", "warnings.slots_overlap", {
          params: { date: key },
          details: { field: "times_by_date", date: key },
          language,
        }),
      );
    }
  }

  if (!strict) {
    if (invalidSlots.length > 0)
      warnings.push(
        aiPollIssue("INVALID_FORMAT", "warnings.invalid_slots_dropped", {
          params: { count: invalidSlots.length },
          details: { field: "time_slots", values: invalidSlots },
          language,
        }),
      );
    if (truncatedSlotLists.length > 0)
      warnings.push(
        aiPollIssue("TOO_MANY_OPTIONS", "warnings.slots_truncated", {
          params: { max: L.maxSlotsPerDate },
          details: { field: "time_slots", scopes: truncatedSlotLists },
          language,
        }),
      );
  }

  // ---- Limites de volume -------------------------------------------------
  if (dates.length > L.maxDates) {
    if (strict) {
      errors.push(
        aiPollError("TOO_MANY_OPTIONS", {
          messageKey: "errors.too_many_dates",
          params: { max: L.maxDates },
          details: { field: "dates", count: dates.length },
          language,
        }),
      );
    } else {
      warnings.push(
        aiPollIssue("TOO_MANY_OPTIONS", "warnings.dates_truncated", {
          params: { max: L.maxDates },
          details: { field: "dates", dropped: dates.slice(L.maxDates) },
          language,
        }),
      );
      dates = dates.slice(0, L.maxDates);
    }
  }

  const slotsFor = (date: string): TimeRange[] =>
    perDate.has(date) ? perDate.get(date)! : globalSlots;
  const optionsOf = (date: string) => Math.max(1, slotsFor(date).length);

  let optionsCount = dates.reduce((n, d) => n + optionsOf(d), 0);
  if (optionsCount > L.maxOptions) {
    if (strict) {
      errors.push(
        aiPollError("TOO_MANY_OPTIONS", {
          messageKey: "errors.too_many_options",
          params: { max: L.maxOptions },
          details: { field: "dates", count: optionsCount },
          language,
        }),
      );
    } else {
      // Dates entières, dans l'ordre chronologique, tant que ça tient.
      const fitting: string[] = [];
      let total = 0;
      for (const d of dates) {
        if (total + optionsOf(d) > L.maxOptions) break;
        fitting.push(d);
        total += optionsOf(d);
      }
      warnings.push(
        aiPollIssue("TOO_MANY_OPTIONS", "warnings.options_truncated", {
          params: { max: L.maxOptions },
          details: { field: "dates", dropped: dates.slice(fitting.length) },
          language,
        }),
      );
      dates = fitting;
      optionsCount = total;
    }
  }

  if (dates.length === 0 && (strict ? rawDates.length === 0 : true)) {
    errors.push(
      aiPollError("INVALID_FORMAT", {
        messageKey: strict ? "errors.no_dates" : "warnings.no_valid_dates",
        details: { field: "dates" },
        language,
      }),
    );
  }

  const slotsByDate: Record<string, TimeRange[]> = {};
  for (const d of dates) {
    const s = slotsFor(d);
    if (s.length > 0) slotsByDate[d] = s;
  }

  return {
    ok: errors.length === 0,
    value: {
      title,
      description,
      language: pollLanguage,
      dates,
      slotsByDate,
      optionsCount: dates.reduce((n, d) => n + optionsOf(d), 0),
    },
    warnings,
    errors,
  };
}
